// VideoTestScreen.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Clock, 
  AlertCircle, 
  CheckCircle, 
  ChevronRight,
  Upload,
  Loader2,
  Video,
  Play,
  Mic
} from 'lucide-react';
import { toast } from 'react-toastify';
import Backdrop from 'components/Backdrop';
import VideoRecorder from './VideoRecorder';
import { 
  startVideoTest, 
  getVideoTestCurrentState, 
  uploadVideoResponse, 
  completeVideoTest 
} from 'api/apiHelper';
import { VideoTestSession, VideoQuestion } from 'types/video-test-types';

interface VideoTestScreenProps {
  onComplete?: () => void;
  testToken?: string;
}

const VideoTestScreen: React.FC<VideoTestScreenProps> = ({ onComplete, testToken: propTestToken }) => {
  const { testToken: paramTestToken } = useParams<{ testToken: string }>();
  const testToken = propTestToken || paramTestToken;
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [testSession, setTestSession] = useState<VideoTestSession | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState<VideoQuestion | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [showNextPrompt, setShowNextPrompt] = useState<boolean>(false);
  
  // New states for start flow
  const [questionStarted, setQuestionStarted] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(0);
  const [showCountdown, setShowCountdown] = useState<boolean>(false);
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);
  const previewVideoRef = React.useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (testToken) {
      initializeTest();
    }
  }, [testToken]);

  // Initialize camera preview for start screen
  useEffect(() => {
    let mounted = true;
    let stream: MediaStream | null = null;
    
    const initPreview = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        if (mounted && !questionStarted && !showCountdown && !showNextPrompt) {
          setPreviewStream(stream);
        } else {
          // Clean up if conditions changed while getting stream
          if (stream) {
            stream.getTracks().forEach(track => track.stop());
          }
        }
      } catch (err) {
        console.log('Preview stream error:', err);
        if (mounted) {
          setPreviewStream(null);
        }
      }
    };

    // Only init preview if not started yet and not showing countdown or next prompt
    if (!questionStarted && !showCountdown && !showNextPrompt) {
      initPreview();
    }

    return () => {
      mounted = false;
      // Clean up stream if component unmounts or dependencies change
      if (stream) {
        stream.getTracks().forEach(track => {
          track.stop();
          track.enabled = false;
        });
      }
    };
  }, [questionStarted, showCountdown, showNextPrompt]);

  // Assign preview stream to video element
  useEffect(() => {
    const videoElement = previewVideoRef.current;
    if (videoElement) {
      if (previewStream) {
        videoElement.srcObject = previewStream;
      } else {
        // Clear the video element when stream is removed
        videoElement.srcObject = null;
      }
    }
    
    return () => {
      // Cleanup: clear srcObject when component unmounts or stream changes
      if (videoElement && videoElement.srcObject) {
        const stream = videoElement.srcObject as MediaStream;
        stream.getTracks().forEach(track => track.stop());
        videoElement.srcObject = null;
      }
    };
  }, [previewStream]);

  // Stop preview stream when question starts (VideoRecorder will take over)
  useEffect(() => {
    if (questionStarted && previewStream) {
      previewStream.getTracks().forEach(track => {
        track.stop();
        track.enabled = false;
      });
      setPreviewStream(null);
    }
  }, [questionStarted, previewStream]);

  const initializeTest = async () => {
    setLoading(true);
    setError('');
    
    try {
      const stateResponse = await getVideoTestCurrentState(testToken!);
      console.log('Current video test state:', stateResponse.data);
      
      if (stateResponse.data.status === 'pending') {
        const startResponse = await startVideoTest(testToken!);
        console.log('Video test started:', startResponse.data);
        setTestSession(startResponse.data);
        setCurrentQuestionFromSession(startResponse.data);
        toast.success('Video test started! Good luck!');
        
      } else if (stateResponse.data.status === 'in_progress') {
        setTestSession(stateResponse.data);
        setCurrentQuestionFromSession(stateResponse.data);
        const startedAt = stateResponse.data.started_at ? new Date(stateResponse.data.started_at) : null;
        const startedMs = startedAt ? Date.now() - startedAt.getTime() : 0;
        const hasProgress = (stateResponse.data.current_question || 0) > 1 ||
          (stateResponse.data.video_responses?.length || 0) > 0;
        const shouldShowResumeToast = hasProgress || startedMs > 60 * 1000;

        if (shouldShowResumeToast) {
          toast.info('Resuming your video test...');
        }
        
      } else if (stateResponse.data.status === 'completed') {
        toast.info('Test already completed. Thank you!');
        if (onComplete) {
          onComplete();
        } else {
          navigate(`/video-test-thank-you/${testToken}`);
        }
        return;
        
      } else if (stateResponse.data.status === 'expired') {
        setError('Test link has expired');
        toast.error('Test link has expired');
        return;
      }
      
    } catch (error: any) {
      console.error('Video test initialization error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to load video test';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const setCurrentQuestionFromSession = (session: VideoTestSession) => {
    if (session.current_question_data) {
      setCurrentQuestion(session.current_question_data);
    } else if (session.questions && session.questions.length > 0) {
      const questionIndex = session.current_question - 1;
      if (questionIndex >= 0 && questionIndex < session.questions.length) {
        setCurrentQuestion(session.questions[questionIndex]);
      }
    }
  };

  const handleRecordingComplete = async (blob: Blob) => {
    setRecordedBlob(blob);
    
    if (!testSession || !currentQuestion) {
      toast.error('Test session not found');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      if (currentQuestion?.id) {
        formData.append('question_id', currentQuestion.id.toString());
      } else {
        toast.error('Question information not available');
        setIsUploading(false);
        return;
      }
      formData.append('video_file', blob, `question_${testSession.current_question}.webm`);

      const response = await uploadVideoResponse(testToken!, formData);
      console.log('Video upload response:', response.data);

      setUploadProgress(100);
      toast.success(`Video uploaded for Question ${testSession.current_question}`);

      if (response.data.is_last_question) {
        await handleCompleteTest();
      } else {
        setShowNextPrompt(true);
      }

    } catch (error: any) {
      console.error('Video upload error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to upload video';
      toast.error(errorMessage);
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
    }
  };

  const handleNextQuestion = async () => {
    setShowNextPrompt(false);
    setRecordedBlob(null);
    setQuestionStarted(false); // Reset for next question
    setShowCountdown(false);
    setCountdown(0);
    
    try {
      const stateResponse = await getVideoTestCurrentState(testToken!);
      setTestSession(stateResponse.data);
      setCurrentQuestionFromSession(stateResponse.data);
    } catch (error: any) {
      console.error('Error fetching next question:', error);
      toast.error('Error loading next question');
    }
  };

  // Handle starting the question with countdown
  const handleStartQuestion = () => {
    setShowCountdown(true);
    setCountdown(5);
    
    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          setShowCountdown(false);
          setQuestionStarted(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleCompleteTest = async () => {
    try {
      const response = await completeVideoTest(testToken!);
      console.log('Test completed:', response.data);
      toast.success('Video assessment completed successfully!');
      // If onComplete callback provided, use it (unified flow), otherwise navigate
      if (onComplete) {
        onComplete();
      } else {
        navigate(`/video-test-thank-you/${testToken}`);
      }
    } catch (error: any) {
      console.error('Error completing test:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to complete test';
      toast.error(errorMessage);
    }
  };

  if (loading) {
    return <Backdrop message="Loading video test..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
          <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-2">Error</h2>
          <p className="text-center text-gray-600 mb-6">{error}</p>
        </div>
      </div>
    );
  }

  if (!testSession) {
    return null;
  }

  // Format time limit for display
  const formatTimeLimit = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (secs === 0) {
      return `${mins} minute${mins !== 1 ? 's' : ''}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const timeLimit = currentQuestion?.time_limit_seconds || 120;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Progress */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-xl font-semibold text-gray-800">Video Assessment</h1>
              <p className="text-sm text-gray-600">Question {testSession.current_question} of {testSession.total_questions}</p>
            </div>
            <div className="flex items-center px-4 py-2 rounded-lg bg-indigo-100">
              <Video className="w-5 h-5 mr-2 text-brand-500" />
              <span className="font-semibold text-brand-500">
                {testSession.current_question}/{testSession.total_questions}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Question Navigation Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-4 sticky top-24">
              <h3 className="font-semibold text-gray-800 mb-3">Questions</h3>
              <div className="grid grid-cols-3 lg:grid-cols-3 gap-2">
                {Array.from({ length: testSession.total_questions }, (_, i) => (
                  <div
                    key={i}
                    className={`
                      w-full h-12 rounded-lg font-semibold flex items-center justify-center
                      ${i < testSession.current_question - 1
                        ? 'bg-green-100 text-green-700'
                        : i === testSession.current_question - 1
                        ? 'bg-brand-500 text-white ring-2 ring-indigo-300'
                        : 'bg-gray-100 text-gray-400'
                      }
                    `}
                  >
                    {i < testSession.current_question - 1 ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      i + 1
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-600">Completed:</span>
                  <span className="font-semibold text-gray-800">{testSession.current_question - 1}/{testSession.total_questions}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Remaining:</span>
                  <span className="font-semibold text-gray-800">{testSession.total_questions - testSession.current_question + 1}</span>
                </div>
              </div>

              {/* Time Info */}
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center text-sm text-gray-600">
                  <Clock className="w-4 h-4 mr-2 text-brand-500" />
                  <span>{formatTimeLimit(timeLimit)} per question</span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-3">
            {showNextPrompt ? (
              // Success - Next Question Prompt
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <div className="flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3">
                  Video Uploaded Successfully!
                </h3>
                <p className="text-gray-600 mb-8">
                  Your response for Question {testSession.current_question} has been saved.
                  {testSession.current_question < testSession.total_questions && (
                    <span className="block mt-2">
                      Ready for Question {testSession.current_question + 1}?
                    </span>
                  )}
                </p>
                <button
                  onClick={handleNextQuestion}
                  className="inline-flex items-center px-8 py-4 bg-brand-500 hover:bg-brand-600 text-white font-semibold rounded-lg transition-all"
                >
                  Continue to Question {testSession.current_question + 1}
                  <ChevronRight className="w-5 h-5 ml-2" />
                </button>
              </div>
            ) : isUploading ? (
              // Uploading State
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <div className="flex items-center justify-center w-20 h-20 bg-indigo-100 rounded-full mx-auto mb-6">
                  <Upload className="w-10 h-10 text-brand-500 animate-bounce" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">
                  Uploading Your Video...
                </h3>
                <p className="text-gray-600 mb-6">
                  Please wait while we upload your response
                </p>
                <div className="w-full bg-gray-200 rounded-full h-3 mb-4 max-w-md mx-auto">
                  <div
                    className="bg-brand-500 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <div className="flex items-center justify-center text-gray-500">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  <span>Processing...</span>
                </div>
              </div>
            ) : showCountdown ? (
              // Countdown Overlay
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="p-6 border-b">
                  <div className="flex items-center justify-between mb-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                      Question {testSession.current_question}
                    </span>
                    <div className="flex items-center text-sm text-gray-500">
                      <Clock className="w-4 h-4 mr-1" />
                      {formatTimeLimit(timeLimit)}
                    </div>
                  </div>
                  <h2 className="text-xl md:text-2xl font-medium text-gray-800 leading-relaxed">
                    {currentQuestion?.question_text || 'Loading question...'}
                  </h2>
                </div>
                <div className="flex flex-col items-center justify-center py-20 bg-gray-900">
                  <div className="text-center">
                    <div className="w-32 h-32 rounded-full bg-brand-500 flex items-center justify-center mx-auto mb-6 animate-pulse">
                      <span className="text-6xl font-bold text-white">{countdown}</span>
                    </div>
                    <p className="text-white text-xl font-medium">Get Ready!</p>
                    <p className="text-gray-400 mt-2">Recording will start automatically</p>
                  </div>
                </div>
              </div>
            ) : !questionStarted ? (
              // Start Question Screen (Before clicking start) with Camera Preview
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                {/* Camera Preview */}
                <div className="relative aspect-video bg-gray-900">
                  <video
                    ref={previewVideoRef}
                    autoPlay
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Overlay with question info */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30">
                    {/* Top info */}
                    <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-white/90 text-gray-800">
                        Question {testSession.current_question} of {testSession.total_questions}
                      </span>
                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-white/90 text-gray-800">
                        <Clock className="w-4 h-4 mr-1.5" />
                        {formatTimeLimit(timeLimit)}
                      </span>
                    </div>
                    
                    {/* Bottom info with start button */}
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-center">
                      <p className="text-white/90 text-sm mb-4">
                        Check your camera position • Click start when ready
                      </p>
                      <button
                        onClick={handleStartQuestion}
                        className="inline-flex items-center px-10 py-4 bg-brand-500 hover:bg-brand-600 text-white font-semibold rounded-lg transition-all text-lg shadow-lg"
                      >
                        <Play className="w-6 h-6 mr-3" />
                        Start Question
                      </button>
                    </div>
                  </div>
                  
                  {/* Loading state if stream not ready */}
                  {!previewStream && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
                      <div className="text-center">
                        <div className="animate-spin w-12 h-12 border-4 border-brand-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                        <p className="text-white">Preparing camera...</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Info Section Below Preview */}
                <div className="p-6">
                  <div className="flex items-center justify-center gap-6 mb-4 text-sm">
                    <span className="flex items-center text-green-600">
                      <Video className="w-4 h-4 mr-1.5" />
                      Camera Ready
                    </span>
                    <span className="flex items-center text-green-600">
                      <Mic className="w-4 h-4 mr-1.5" />
                      Microphone Ready
                    </span>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-700 mb-2 text-center">What happens next?</h4>
                    <ul className="text-sm text-gray-600 space-y-1 text-center">
                      <li>1. Click "Start Question" to reveal the question</li>
                      <li>2. A 5-second countdown will begin</li>
                      <li>3. Recording starts automatically after countdown</li>
                    </ul>
                  </div>
                </div>
              </div>
            ) : (
              // Video Recorder (After countdown)
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                {/* Question Display */}
                <div className="p-6 border-b">
                  <div className="flex items-center justify-between mb-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                      Question {testSession.current_question}
                    </span>
                    <div className="flex items-center text-sm text-gray-500">
                      <Clock className="w-4 h-4 mr-1" />
                      {formatTimeLimit(timeLimit)}
                    </div>
                  </div>
                  <h2 className="text-xl md:text-2xl font-medium text-gray-800 leading-relaxed">
                    {currentQuestion?.question_text || 'Loading question...'}
                  </h2>
                </div>

                {/* Video Recorder Component - autoStart is true now */}
                <VideoRecorder
                  key={`${testSession.current_question}-${questionStarted}`}
                  onRecordingComplete={handleRecordingComplete}
                  timeLimitSeconds={currentQuestion?.time_limit_seconds || 120}
                  autoStart={true}
                  questionNumber={testSession.current_question}
                />
              </div>
            )}

            {/* Tips Box - only show on start screen */}
            {!showNextPrompt && !isUploading && !questionStarted && !showCountdown && (
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <AlertCircle className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-1">Before You Start</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Ensure you're in a quiet environment with good lighting</li>
                      <li>• The question will be revealed when you click "Start Question"</li>
                      <li>• You cannot pause or redo once recording begins</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoTestScreen;
